'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SessionContext } from '@benzinga/session-context';
import type { QuoteSessionType, PeerQuote } from '@benzinga/quotes-manager';
import { Button, SectionTitle } from '@benzinga/core-ui';
import { QUOTE_PEER_QUOTES_PREVIEW_LIMIT } from '../../utils/quote/peerQuotesConstants';
import Peer from './Peer';

const PeerRowSkeleton: React.FC = () => (
  <div className="mb-2 w-full rounded-xl border border-[#ceddf2] bg-white px-2 py-1.5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="h-3.5 w-28 rounded bg-gray-200" />
        <div className="h-3 w-20 rounded bg-gray-100" />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <div className="h-3.5 w-14 rounded bg-gray-200" />
        <div className="h-3 w-10 rounded bg-gray-100" />
      </div>
    </div>
    <div className="mt-2 h-[50px] w-full rounded bg-gray-100" />
  </div>
);

interface Props {
  peers: Record<string, PeerQuote>;
  peerSymbols: string[];
  onAvailabilityChange?: (available: boolean) => void;
  showTitle?: boolean;
  symbol: string;
  type: QuoteSessionType;
}

export const Peers: React.FC<Props> = ({
  peers: initialPeers,
  peerSymbols,
  onAvailabilityChange,
  showTitle = true,
  symbol,
  type,
}) => {
  const { t } = useTranslation('quote');
  const session = React.useContext(SessionContext);
  const [rowsCount, setRowsCount] = React.useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 760 ? 4 : 7,
  );
  const [showingAll, setShowingAll] = React.useState(false);
  const [fetchedPeers, setFetchedPeers] = React.useState<{ peers: Record<string, PeerQuote>; symbol: string } | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const [initialFetchSettled, setInitialFetchSettled] = React.useState(false);
  const initialFetchAttemptedForSymbol = React.useRef<string | null>(null);
  const onAvailabilityChangeRef = React.useRef(onAvailabilityChange);
  onAvailabilityChangeRef.current = onAvailabilityChange;

  const peers = fetchedPeers?.symbol === symbol ? fetchedPeers.peers : initialPeers;
  const hasPeers = Object.keys(peers).length > 0;

  const filteredPeerSymbols = React.useMemo(
    () => peerSymbols.filter(peerSymbol => peerSymbol !== symbol),
    [peerSymbols, symbol],
  );
  const wantsClientPeers = filteredPeerSymbols.length > 0 && Object.keys(initialPeers).length === 0;
  const showPeersSkeleton = wantsClientPeers && !hasPeers && !initialFetchSettled;

  React.useLayoutEffect(() => {
    initialFetchAttemptedForSymbol.current = null;
    setInitialFetchSettled(false);
  }, [symbol, peerSymbols]);

  React.useEffect(() => {
    const newRowsCount = window.innerWidth <= 760 ? 4 : 7;
    setRowsCount(newRowsCount);
  }, []);

  React.useEffect(() => {
    if (!symbol || !wantsClientPeers || initialFetchAttemptedForSymbol.current === symbol || hasPeers) return;

    let cancelled = false;
    let initialFetchReturnedPeers = false;
    initialFetchAttemptedForSymbol.current = symbol;

    const fetchInitialPeers = async () => {
      setLoading(true);
      try {
        const { QuotesManager } = await import('@benzinga/quotes-manager');
        const response = await session.getManager(QuotesManager).getPeerQuotes(symbol, QUOTE_PEER_QUOTES_PREVIEW_LIMIT);
        if (response.err) {
          throw response.err;
        }
        const peers = response.ok?.data.peers ?? {};
        if (!cancelled && Object.keys(peers).length > 0) {
          initialFetchReturnedPeers = true;
          setFetchedPeers({ peers, symbol });
        } else if (!cancelled) {
          initialFetchAttemptedForSymbol.current = null;
          onAvailabilityChangeRef.current?.(false);
        }
      } catch (error) {
        if (!cancelled) {
          initialFetchAttemptedForSymbol.current = null;
          onAvailabilityChangeRef.current?.(false);
        }
        console.error('Error fetching peers:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialFetchSettled(true);
        }
      }
    };

    fetchInitialPeers();

    return () => {
      cancelled = true;
      if (!initialFetchReturnedPeers && initialFetchAttemptedForSymbol.current === symbol) {
        initialFetchAttemptedForSymbol.current = null;
      }
    };
  }, [hasPeers, session, symbol, peerSymbols, wantsClientPeers, initialPeers]);

  const handleShowAll = async () => {
    const expectedPeerCount = peerSymbols.length;
    if (Object.keys(peers).length < expectedPeerCount && !loading) {
      setLoading(true);
      try {
        const { QuotesManager } = await import('@benzinga/quotes-manager');
        const response = await session.getManager(QuotesManager).getPeerQuotes(symbol);
        if (response.err) {
          throw response.err;
        }
        const peers = response.ok?.data.peers ?? {};
        if (Object.keys(peers).length > 0) {
          setFetchedPeers({ peers, symbol });
          setShowingAll(true);
        }
      } catch (error) {
        console.error('Error fetching peers:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  if (!symbol) return null;

  const visiblePeerSymbols = filteredPeerSymbols.filter(peerSymbol => peers[peerSymbol]);

  if (filteredPeerSymbols.length === 0) return null;
  if (!showPeersSkeleton && !loading && visiblePeerSymbols.length === 0) return null;

  const skeletonCount = Math.min(filteredPeerSymbols.length, rowsCount);

  return (
    <div className="overflow-auto">
      {showTitle ? (
        <SectionTitle level={3} size="xl" uppercase={false}>
          {t('Quote.Peers.people-also-watch')}
        </SectionTitle>
      ) : null}
      {showPeersSkeleton ? (
        <div
          className={`flex w-full animate-pulse flex-col ${showTitle ? 'mt-6' : 'mt-2'}`}
          aria-busy="true"
          aria-label={t('Quote.Peers.people-also-watch')}
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <PeerRowSkeleton key={index} />
          ))}
        </div>
      ) : (
        <>
          <div
            className={`w-full text-sm leading-normal max-h-[1160px] overflow-y-auto no-scrollbar mb-2 ${showTitle ? 'mt-6' : 'mt-2'}`}
          >
            {visiblePeerSymbols.map((peerSymbol: string) => (
              <Peer peerData={peers[peerSymbol]} currentSymbol={peerSymbol} key={`${peerSymbol}-day`} type={type} />
            ))}
          </div>
          {!showingAll && filteredPeerSymbols.length > rowsCount && (
            <Button
              className="flex items-center justify-center text-sm w-full text-center h-[40px]"
              disabled={loading}
              onClick={handleShowAll}
              title={t('Quote.Peers.show-all-related-companies', { companiesLength: filteredPeerSymbols.length })}
              variant="flat-light-blue-outlined"
              isLoading={loading}
            >
              <span>
                {t('Quote.Peers.show-all-related-companies', { companiesLength: filteredPeerSymbols.length })}
              </span>
              <svg className="h-6 w-6 fill-current text-grey-dark" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default Peers;
